import {useMemo} from "react";
import {Link} from "react-router";
import {Castle, Plus} from "lucide-react";
import {parseAsString, useQueryState} from "nuqs";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {useDungeonsQuery} from "@/dungeons/DungeonQueries.ts";
import DungeonCard from "@/dungeons/DungeonCard.tsx";
import SearchBar from "@/search/SearchBar.tsx";
import {Button} from "@/components/ui/button.tsx";

export default function DungeonEmpty() {
    const {campaignPath} = useCampaign();
    const [name, setName] = useQueryState("name", parseAsString.withDefault(""));
    const filter = useMemo(() => ({name: name.trim()}), [name]);
    const query = useDungeonsQuery(filter, 30);

    return <main className="min-h-0 grow overflow-auto p-6 md:p-10">
        <title>Dungeons verwalten | DireDawn</title>
        <div className="mx-auto max-w-5xl space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">

                <div className="flex flex-row items-center gap-3">
                    <Castle className="size-8 text-success"/>
                    <h1 className="text-3xl font-semibold tracking-tight">Dungeons</h1>
                </div>
                <Button asChild><Link to={campaignPath("dungeons/new")}><Plus/>Neuer Dungeon</Link></Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="w-full max-w-sm"><SearchBar value={name} onChange={setName}
                                                            placeholder="Dungeons suchen"/></div>
                {!query.isPending && !query.isError &&
                    <p className="text-sm text-muted-foreground">{query.totalCount} Dungeons</p>}
            </div>
            {query.isPending && <p role="status" className="text-muted-foreground">Dungeons werden geladen…</p>}
            {query.isError && <div role="alert" className="flex flex-wrap items-center gap-3 text-red-400">
                <span>Dungeons konnten nicht geladen werden.</span>
                <Button variant="outline" size="sm" onClick={() => void query.refetch()}>Erneut laden</Button>
            </div>}
            {!query.isPending && !query.isError && query.dungeons.length === 0 &&
                <div className="rounded-xl border border-border bg-card/60 p-8 text-center text-muted-foreground">
                    {name.trim() ? "Keine Dungeons entsprechen der Suche." : "Noch keine Dungeons vorhanden. Lege deinen ersten Dungeon an."}
                </div>}
            {query.dungeons.length > 0 && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {query.dungeons.map(dungeon => <DungeonCard key={dungeon.id} dungeon={dungeon}
                                                            to={campaignPath(`dungeons/${dungeon.id}`)}/>)}
            </div>}
            {query.hasNextPage && <div className="text-center">
                <Button variant="outline" disabled={query.isFetchingNextPage} onClick={() => void query.loadMore()}>
                    {query.isFetchingNextPage ? "Lade…" : "Weitere Dungeons laden"}
                </Button>
            </div>}
        </div>
    </main>;
}
